import { GRAYSCALE_400 } from '@styles/colors'
import { INTER } from '@styles/fonts'
import { forwardRef } from 'react'
import { Container, TitleContainer, OptionalText, InputWrapper, StyledInput, ErrorText, ClearButton } from './AccountNumberInput.styles'
import { useTranslation } from 'react-i18next'

const AccountNumberInput = forwardRef<HTMLInputElement, {
  title?: string
  value?: string
  isOptional?: boolean
  isError?: boolean
  errorText?: string
  borderless?: boolean
  placeholder?: string
  fontSize?: string
  fontFamily?: string
  inputMode?: 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url'
  disabled?: boolean
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onClear?: () => void
}>(({
  title,
  value,
  isOptional = false,
  isError = false,
  errorText,
  borderless = false,
  fontSize = '13px',
  fontFamily = INTER.regular,
  placeholder,
  inputMode,
  disabled,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  onClear
}, ref) => {
  const { t } = useTranslation()
  const defaultTitle = t('components.textInput.defaultTitle')
  const defaultErrorText = t('components.textInput.defaultErrorText')
  const defaultPlaceholder = t('components.textInput.defaultPlaceholder')

  const handleClear = () => {
    if (onClear) {
      onClear()
    } else {
      // Create a synthetic event to clear the input
      const syntheticEvent = {
        target: { value: '' },
        currentTarget: { value: '' }
      } as React.ChangeEvent<HTMLInputElement>
      onChange(syntheticEvent)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numbers = e.target.value.replace(/[^0-9]/g, '').replace(/^0+/, '')
    const syntheticEvent = {
      ...e,
      target: { ...e.target, value: numbers },
      currentTarget: { ...e.currentTarget, value: numbers }
    } as React.ChangeEvent<HTMLInputElement>
    onChange(syntheticEvent)
  }

  return (
    <Container>
      <TitleContainer>
        {title || defaultTitle}
        {isOptional && <OptionalText>{t('components.textInput.optional')}</OptionalText>}
      </TitleContainer>
      <InputWrapper $isError={isError} $borderless={borderless}>
        <StyledInput
          ref={ref}
          placeholder={placeholder || defaultPlaceholder}
          value={value || ''}
          onChange={handleChange}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          inputMode={inputMode}
          disabled={disabled}
          $isError={isError}
          style={{
            fontSize,
            fontFamily
          }}
        />
        {value && value.length > 0 && (
          <ClearButton onClick={handleClear} type="button">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 18C5.59 18 2 14.41 2 10C2 5.59 5.59 2 10 2C14.41 2 18 5.59 18 10C18 14.41 14.41 18 10 18ZM10 0C4.47 0 0 4.47 0 10C0 15.53 4.47 20 10 20C15.53 20 20 15.53 20 10C20 4.47 15.53 0 10 0ZM12.59 6L10 8.59L7.41 6L6 7.41L8.59 10L6 12.59L7.41 14L10 11.41L12.59 14L14 12.59L11.41 10L14 7.41L12.59 6Z"
                fill={GRAYSCALE_400}
              />
            </svg>
          </ClearButton>
        )}
      </InputWrapper>
      {isError && <ErrorText>{errorText || defaultErrorText}</ErrorText>}
    </Container>
  )
})

export default AccountNumberInput
